import * as _ from 'underscore';

import { ContentSortOptions } from '../models/ContentSortOptions';
import { IOrderClause } from '../ServerApi/types/IOrderClause';

export module Order {

  export let MaxLength = 3;

  function clauseFromInput(input: string): IOrderClause {
    const parts: string[] = input.split(':');
    if (parts.length === 2) {
      return {
        field: parts[0], // TODO: validate field value
        ascending: parts[1] === 'asc'
      };
    } else {
      return null;
    }
  }

  // If possibleOrderKeys does not exist then allow all keys
  function isValidOrderField(field: string, possibleOrderKeys?: ArrayLike<string>) {
    return _.isUndefined(possibleOrderKeys) ? true : _(possibleOrderKeys).contains(field);
  }

  export function fromInput(input: string, possibleOrderKeys?: ArrayLike<string>): IOrderClause[] {
    const clauses: IOrderClause[] = [];
    if (_.isString(input)) {
      _.each(input.split(','), (clauseString: string) => {
        const clause = clauseFromInput(clauseString);
        if (clause && isValidOrderField(clause.field, possibleOrderKeys) && clauses.length < Order.MaxLength) {
          clauses.push(clause);
        }
      });
    }
    return _.isEmpty(clauses) ? null : clauses;
  }

  export function startsWithField(clauses: ReadonlyArray<IOrderClause>, field: string): boolean {
    return !!clauses && clauses.length > 0 && clauses[0].field === field;
  }

  export function startsWithRelevantField(clauses: ReadonlyArray<IOrderClause>, possibleFields: ArrayLike<string>) : string {
    for (let i = 0; i < possibleFields.length; ++i) {
      if (Order.startsWithField(clauses, possibleFields[i])){
        return possibleFields[i];
      }
    }
    return '';
  }

  export function withFirstClauseToggled(clauses: ReadonlyArray<IOrderClause>): IOrderClause[] {
    const newClauses: IOrderClause[] = _.map(clauses, _.clone);

    if (newClauses.length > 0 && ContentSortOptions.supportsAscAndDesc(newClauses[0].field)) {
      newClauses[0].ascending = !newClauses[0].ascending;
    }

    return newClauses;
}

  export function withClausePrepended(clauses: ReadonlyArray<IOrderClause>, newClause: IOrderClause): IOrderClause[] {
    let newClauses: IOrderClause[];
    if (newClause) {
      // remove any existing clauses for the given field (note that reject returns a copy)
      newClauses = _.reject(clauses, (clause: IOrderClause) => clause.field === newClause.field);
      // prepend the given clause
      newClauses.unshift(newClause);
      // enforce the maximum length
      if (newClauses.length > Order.MaxLength) {
        newClauses.length = Order.MaxLength;
      }
    } else {
      newClauses = _.clone(clauses as IOrderClause[]);
    }
    return newClauses;
  }

  export function asc(field: string): IOrderClause {
    return { field: field, ascending: true };
  }

  export function desc(field: string): IOrderClause {
    return { field: field, ascending: false };
  }

  export function type(type: string) {
    return {
      asc: (field: string) => {
        return {
          type,
          ...Order.asc(field)
        };
      },
      desc: (field: string) => {
        return {
          type,
          ...Order.desc(field)
        };
      }
    };
  }

  export function isColumnAscending(direction: string) {
    return direction === 'asc';
  }

  export function flipColumn(direction: string): string {
    return isColumnAscending(direction) ? 'desc': 'asc';
  }

  export function withoutField(order: ReadonlyArray<IOrderClause>, field: string): ReadonlyArray<IOrderClause> {
    if (!field) return order;
    return _.reject(order, (orderClause: IOrderClause) => orderClause.field === field);
  }

  /**
   * Finds the order clause for the field in the order. Returns undefined if no clause is found.
   */
  export function findOrderClauseForField(order: ReadonlyArray<IOrderClause>, field: string): IOrderClause {
    return _.findWhere(order, {field: field});
  }

  export function serialize(order: ReadonlyArray<IOrderClause>) {
    return _.map(order, serializeOrderClause).join(',');
  }

  function serializeOrderClause(clause: IOrderClause) {
    return clause.field + ':' + (clause.ascending ? 'asc' : 'desc');
  }

}

